import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, User, DollarSign, Calendar, Save } from 'lucide-react';

const PaymentFormModal = ({ isOpen, onClose, onSave, paymentToEdit, members }) => {
  const [formData, setFormData] = useState({
    memberId: '',
    memberName: '', // Para mostrar el nombre del socio seleccionado
    membershipType: 'basic',
    amount: '',
    status: 'completed',
    paymentDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    paymentMethod: 'wechat'
  });

  useEffect(() => {
    if (paymentToEdit) {
      setFormData(paymentToEdit);
    } else {
      setFormData({
        memberId: '',
        memberName: '',
        membershipType: 'basic',
        amount: '',
        status: 'completed',
        paymentDate: new Date().toISOString().slice(0, 10),
        dueDate: '',
        paymentMethod: 'wechat'
      });
    }
  }, [paymentToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'memberId') {
      const selectedMember = members.find(m => m.id === parseInt(value));
      if (selectedMember) {
        setFormData(prev => ({
          ...prev,
          memberName: selectedMember.name,
          membershipType: selectedMember.membershipType,
          amount: getMembershipAmount(selectedMember.membershipType),
          dueDate: calculateDueDate(selectedMember.joinDate || new Date().toISOString().slice(0, 10)) // Usar joinDate o fecha actual
        }));
      } else {
        // Si no se encuentra el socio (por ejemplo, si se selecciona la opción vacía)
        setFormData(prev => ({
          ...prev,
          memberName: '',
          membershipType: 'basic',
          amount: '',
          dueDate: ''
        }));
      }
    }
  };

  const getMembershipAmount = (type) => {
    switch (type) {
      case 'basic': return 100;
      case 'premium': return 300;
      case 'vip': return 500;
      default: return 0;
    }
  };

  const calculateDueDate = (startDate) => {
    const date = new Date(startDate);
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().slice(0, 10);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-3xl p-8 shadow-2xl w-full max-w-2xl relative"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {paymentToEdit ? 'Editar Pago' : 'Registrar Nuevo Pago'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Socio</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <Select
                    options={members.map(member => ({
                      value: member.id,
                      label: member.rut ? `${member.name} (${member.rut})` : member.name
                    }))}
                    placeholder="Buscar y seleccionar socio..."
                    isClearable
                    isDisabled={!!paymentToEdit}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        paddingLeft: '2.5rem',
                        minHeight: '3rem',
                        borderRadius: '0.75rem',
                        borderColor: '#e5e7eb',
                        boxShadow: 'none',
                        backgroundColor: '#f9fafb',
                        fontWeight: '500',
                        fontSize: '1rem',
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: '1rem',
                        backgroundColor: state.isSelected ? '#e0e7ff' : state.isFocused ? '#f3f4f6' : '#fff',
                        color: '#1f2937',
                      }),
                    }}
                    value={members.length > 0 && formData.memberId ? {
                      value: formData.memberId,
                      label: (() => {
                        const member = members.find(m => m.id === parseInt(formData.memberId));
                        return member ? (member.rut ? `${member.name} (${member.rut})` : member.name) : '';
                      })()
                    } : null}
                    onChange={option => {
                      if (option) {
                        const selectedMember = members.find(m => m.id === option.value);
                        setFormData(prev => ({
                          ...prev,
                          memberId: option.value,
                          memberName: selectedMember ? selectedMember.name : '',
                          membershipType: selectedMember ? selectedMember.membershipType : 'basic',
                          amount: selectedMember ? getMembershipAmount(selectedMember.membershipType) : '',
                          dueDate: selectedMember ? calculateDueDate(selectedMember.joinDate || new Date().toISOString().slice(0, 10)) : ''
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          memberId: '',
                          memberName: '',
                          membershipType: 'basic',
                          amount: '',
                          dueDate: ''
                        }));
                      }
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Membresía</label>
                  <select
                    name="membershipType"
                    value={formData.membershipType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                    required
                    disabled={!!paymentToEdit} // Deshabilitar si se está editando un pago
                  >
                    <option value="basic">Básica</option>
                    <option value="premium">Premium</option>
                    <option value="vip">VIP</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monto ($)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="Monto del pago"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Pago</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      name="paymentDate"
                      value={formData.paymentDate}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Vencimiento</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                  >
                    <option value="completed">Completado</option>
                    <option value="pending">Pendiente</option>
                    <option value="overdue">Vencido</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago</label>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                  >
                    <option value="wechat">WeChat Pay</option>
                    <option value="alipay">Efectivo</option>
                    <option value="bank">Transferencia Bancaria</option>
                  </select>
                </div>
              </div>

              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Save className="w-5 h-5" />
                Guardar Pago
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentFormModal;